import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Container,
  Button,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
} from '@mui/material';
import { Edit, X, Plus, Search, Filter } from 'lucide-react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { UserRegistrationForm } from "./DriverCreate";
import Header from '../Header';
import dayjs from 'dayjs';
import { local_signage } from '../Const';

const DriverListView = () => {
  const [drivers, setDrivers] = useState([]);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Fetch driver data from API
  const fetchDrivers = async () => {
    try {
      const response = await fetch(`${local_signage}/api/v1/contractemplist?BranchID=ALL&CompanyCode=ALL&vehicleName=ALL`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('AuthorizationToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Error fetching data: ${response.statusText}`);
      }

      const data = await response.json();
      setDrivers(data);
      setFilteredDrivers(data);
    } catch (error) {
      console.error("Error fetching driver data:", error);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, []);

  useEffect(() => {
    let filtered = drivers.filter((driver) =>
      driver.userid.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.UserName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Apply status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(driver => driver.IsActive === true);
    } else if (statusFilter === '') {
      filtered = filtered.filter(driver => driver.IsActive === false);
    }

    setFilteredDrivers(filtered);
    setPage(0); // Reset to first page when filters change
  }, [searchTerm, statusFilter, drivers]);

  const handleEdit = (driver) => {
    setSelectedDriver(driver);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedDriver(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDriver(null);
    fetchDrivers(); // Refresh driver list
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  // Get current page data
  const paginatedDrivers = filteredDrivers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const formatDate = (dateString) => {
    return dayjs(dateString).format('DD/MM/YYYY');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Header />
      <Box sx={{ 
        padding: { xs: '8px', sm: '16px', md: '14px' }, 
        backgroundColor: theme.palette.background.default,
        minHeight: '100vh',
        width: '100%'
      }}>
        <Container maxWidth="xl" sx={{ 
          paddingLeft: { xs: '8px', sm: '16px' },
          paddingRight: { xs: '8px', sm: '16px' }
        }}>
          <Card sx={{
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            borderRadius: '16px',
            transition: 'transform 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-2px)',
            },
          }}>
            <CardHeader
              title={
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  flexWrap: 'wrap',
                  gap: '16px',
                  [theme.breakpoints.down('md')]: {
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  },
                }}>
                  <Box sx={{ 
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, 
                    fontWeight: 600 
                  }}>
                    User List
                  </Box>
                  <Box sx={{
                    display: 'flex',
                    gap: '16px',
                    alignItems: 'center',
                    [theme.breakpoints.down('sm')]: {
                      flexDirection: 'column',
                      gap: '8px',
                      width: '100%',
                    },
                  }}>
                    {/* Search Input with Icon Inside */}
                    <TextField
                      variant="outlined"
                      placeholder="Search by User ID or Name"
                      size="small"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{ 
                        width: { xs: '100%', sm: '250px' },
                        minWidth: '200px'
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search size={20} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    {/* Add New User Button */}
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: '#004D00',
                        color: '#FFFFFF',
                        '&:hover': { backgroundColor: '#003B00' },
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: 'fit-content',
                      }}
                      startIcon={<Plus size={20} />}
                      onClick={handleCreate}
                    >
                      Add New User
                    </Button>

                    {/* Status Filter */}
                    <FormControl size="small" sx={{ 
                      minWidth: '140px',
                      width: { xs: '100%', sm: '140px' }
                    }}>
                      <InputLabel id="status-filter-label">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Filter size={16} />
                          Status
                        </Box>
                      </InputLabel>
                      <Select
                        labelId="status-filter-label"
                        value={statusFilter}
                        onChange={handleStatusFilterChange}
                        label="Status"
                        sx={{
                          '& .MuiSelect-select': {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }
                        }}
                      >
                        <MenuItem value="all">All Users</MenuItem>
                        <MenuItem value="active">Active Only</MenuItem>
                        <MenuItem value="inactive">Inactive Only</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              }
              sx={{
                backgroundColor: '#d5d4d8',
                color: '#000000cf',
                padding: { xs: '8px', sm: '16px' },
              }}
            />

            <CardContent sx={{ 
              padding: { xs: '8px', sm: '16px' },
              '&:last-child': { paddingBottom: { xs: '8px', sm: '16px' } }
            }}>
              <Box sx={{
                width: '100%',
                height: '70vh', // Fixed height for vertical scroll
                maxHeight: '600px',
                overflow: 'auto', // Both horizontal and vertical scroll
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#888',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: '#555',
                  },
                },
                '&::-webkit-scrollbar-corner': {
                  backgroundColor: '#f1f1f1',
                },
              }}>
                <Table stickyHeader sx={{
                  minWidth: '1000px', // Increased for wider username column
                  tableLayout: 'fixed', // Fixed layout for better performance
                  '& .MuiTableCell-root': {
                    padding: '8px 12px',
                    fontSize: '0.875rem',
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #e0e0e0',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                  '& .MuiTableCell-head': {
                    backgroundColor: '#f5f5f5',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: '#333',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    borderBottom: '2px solid #ddd',
                  },
                  '& .MuiTableRow-root': {
                    '&:hover': {
                      backgroundColor: '#f8f9fa',
                    },
                  },
                  '& .MuiTableBody-root .MuiTableRow-root': {
                    height: '48px', // Fixed row height
                  },
                }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        width: '350px', // Increased width for full name visibility
                        minWidth: '300px',
                        fontWeight: 600,
                      }}>
                        Name
                      </TableCell>
                      <TableCell sx={{ 
                        width: '80px',
                        minWidth: '80px',
                      }}>
                        Branch
                      </TableCell>
                      <TableCell sx={{ 
                        width: '120px',
                        minWidth: '120px',
                        fontFamily: 'monospace',
                      }}>
                        User ID
                      </TableCell>
                      <TableCell sx={{ 
                        width: '80px',
                        minWidth: '80px',
                      }}>
                        Company
                      </TableCell>
                      <TableCell sx={{ 
                        width: '120px',
                        minWidth: '120px',
                      }}>
                        DOB
                      </TableCell>
                      <TableCell sx={{ 
                        width: '120px',
                        minWidth: '120px',
                      }}>
                        Join Date
                      </TableCell>
                      <TableCell sx={{ 
                        width: '80px',
                        minWidth: '80px',
                      }}>
                        Status
                      </TableCell>
                      <TableCell align="right" sx={{ 
                        width: '70px',
                        minWidth: '70px',
                      }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedDrivers.length > 0 ? (
                      paginatedDrivers.map((driver) => (
                        <TableRow key={driver.userid}>
                          <TableCell sx={{
                            width: '250px',
                            minWidth: '250px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                          }} 
                          title={driver.UserName}>
                            {driver.UserName}
                          </TableCell>
                          <TableCell sx={{
                            width: '120px',
                            minWidth: '120px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }} 
                          title={driver.CurrentBranchId}>
                            {driver.CurrentBranchId}
                          </TableCell>
                          <TableCell sx={{
                            width: '120px',
                            minWidth: '120px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontFamily: 'monospace',
                          }} 
                          title={driver.userid}>
                            {driver.userid}
                          </TableCell>
                          <TableCell sx={{
                            width: '120px',
                            minWidth: '120px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }} 
                          title={driver.CompanyCode}>
                            {driver.CompanyCode}
                          </TableCell>
                          <TableCell sx={{
                            width: '120px',
                            minWidth: '120px',
                          }}>
                            {formatDate(driver.DOB)}
                          </TableCell>
                          <TableCell sx={{
                            width: '120px',
                            minWidth: '120px',
                          }}>
                            {formatDate(driver.JDate)}
                          </TableCell>
                          <TableCell sx={{
                            width: '100px',
                            minWidth: '100px',
                          }}>
                            <Box sx={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              backgroundColor: driver.IsActive ? '#e8f5e8' : '#ffebee',
                              color: driver.IsActive ? '#2e7d32' : '#c62828',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              display: 'inline-block',
                              minWidth: '60px',
                              textAlign: 'center',
                            }}>
                              {driver.IsActive ? 'Active' : 'Inactive'}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{
                            width: '100px',
                            minWidth: '100px',
                            padding: '4px 8px',
                          }}>
                            <IconButton 
                              onClick={() => handleEdit(driver)} 
                              sx={{
                                color: theme.palette.primary.main,
                                padding: '4px',
                                '&:hover': {
                                  backgroundColor: theme.palette.primary.light + '20',
                                },
                              }}
                              size="small"
                            >
                              <Edit size={16} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ 
                          padding: '32px 16px',
                          color: '#666',
                          fontStyle: 'italic',
                        }}>
                          {searchTerm || statusFilter !== 'all' ? 'No users found matching your criteria.' : 'No users found.'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
              
              {/* Pagination */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mt: 2,
                px: 1,
                flexWrap: 'wrap',
                gap: 1,
              }}>
                <Box sx={{ 
                  fontSize: '0.875rem', 
                  color: '#666',
                  order: { xs: 2, sm: 1 }
                }}>
                  Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, filteredDrivers.length)} of {filteredDrivers.length} users
                  {(searchTerm || statusFilter !== 'all') && ` (filtered from ${drivers.length} total)`}
                </Box>
                
                <TablePagination
                  component="div"
                  count={filteredDrivers.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  sx={{
                    order: { xs: 1, sm: 2 },
                    '& .MuiTablePagination-toolbar': {
                      minHeight: '52px',
                    },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontSize: '0.875rem',
                    },
                    '& .MuiTablePagination-select': {
                      fontSize: '0.875rem',
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Modal for Add/Edit User */}
          <Dialog
            open={isModalOpen}
            onClose={handleCloseModal}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            sx={{
              '& .MuiDialog-paper': {
                margin: { xs: 0, sm: '16px' },
                width: { xs: '100%', sm: 'auto' },
                maxHeight: '90vh',
                borderRadius: { xs: 0, sm: '8px' },
              },
              '& .MuiBackdrop-root': {
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
              },
            }}
          >
            <DialogTitle sx={{ 
              margin: 0, 
              padding: '16px',
              borderBottom: '1px solid #e0e0e0',
              fontSize: '1.25rem',
              fontWeight: 600,
            }}>
              {selectedDriver ? 'Edit User' : 'Add New User'}
              <IconButton
                onClick={handleCloseModal}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: '#666',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                <X size={20} />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{
              padding: '0',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: '3px',
                '&:hover': {
                  backgroundColor: '#555',
                },
              },
            }}>
              <Box sx={{ 
                padding: '16px',
                minHeight: '200px',
              }}>
                {isModalOpen && (
                  <UserRegistrationForm
                    initialData={selectedDriver}
                    onClose={handleCloseModal}
                  />
                )}
              </Box>
            </DialogContent>
          </Dialog>
        </Container>
      </Box>
    </LocalizationProvider>
  );
};

export default DriverListView;